import { useState, useEffect } from 'react'

import { getLenderDisclosures } from 'api/lender'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import DisclosureType from 'state/enums/DisclosureType'

export interface UseLenderDisclosureInterface {
    disclosureText: string | undefined
    loading: boolean
    error: Error | null
}

class MissingLenderDisclosureError extends Error {}

const useLenderDisclosure = (
    disclosureName: DisclosureType,
    lenderId?: number,
    applicationId?: number,
    enabled = true,
): UseLenderDisclosureInterface => {
    const [disclosureText, setDisclosureText] = useState<string>()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        const loadLenderDisclosures = async () => {
            const result = await getLenderDisclosures(lenderId, applicationId)

            const disclosure = result.data.find(
                (item) => item.name === disclosureName,
            )

            if (!disclosure) {
                throw new MissingLenderDisclosureError(
                    `Disclosure with name "${disclosureName}" not found.`,
                )
            }

            return disclosure.text
        }

        if (enabled && lenderId) {
            loadLenderDisclosures()
                .then((disclosure: string) => {
                    setError(null)
                    setDisclosureText(disclosure)
                })
                .catch((disclosureError) => {
                    // Application filtering can omit approval disclosures.
                    // Preserve the existing error state without logging these omissions.
                    const isApprovalDisclosureOmission =
                        disclosureError instanceof
                            MissingLenderDisclosureError &&
                        !!applicationId &&
                        (disclosureName ===
                            DisclosureType.CREDIT_SOFT_PULL_APPLICATION_APPROVAL ||
                            disclosureName ===
                                DisclosureType.CREDIT_SAFECHECK_SOFT_PULL_APPLICATION_APPROVAL)

                    if (!isApprovalDisclosureOmission) {
                        OctaneLogger.error(
                            'Error when getting disclosure text.',
                            {
                                application: {
                                    id: applicationId,
                                },
                                lender: {
                                    id: lenderId,
                                },
                                disclosureName,
                            },
                            disclosureError as Error,
                        )
                    }

                    setError(disclosureError)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [applicationId, lenderId, disclosureName, enabled])

    return {
        disclosureText,
        loading,
        error,
    }
}

export default useLenderDisclosure
